import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ApplyLeaveTab from "../components/ApplyLeaveTab";
import ApproveLeaveTab from "../components/ApproveLeaveTab";
import DashboardTab from "../components/DashboardTab";
import ProfileTab from "../components/ProfileTab";
import AllProfilesTab from "../components/AllProfilesTab";
import AddUserModal from "../components/AddUserModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingLeaveId, setEditingLeaveId] = useState(null);

  const token = localStorage.getItem("token");

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  useEffect(() => {
    if (user.role === "admin" || user.role == "manager") {
      setActiveTab("dashboard");
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (user.role === "admin" || user.role === "manager") {
        const usersResponse = await axios.get(`${API}/users`, axiosConfig);
        if (usersResponse.data.success) {
          setUsers(usersResponse.data.data.users);
        }
      }

      const leavesResponse = await axios.get(`${API}/leaves`, axiosConfig);
      if (leavesResponse.data.success) {
        setLeaves(leavesResponse.data.data.leaves);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLeave = async (leaveId, status) => {
    try {
      const response = await axios.patch(
        `${API}/leaves/${leaveId}/approve`,
        { status },
        axiosConfig
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error approving leave:", error);
      toast.error("Failed to update leave status");
    }
  };

  const getTabs = () => {
    if (user.role === "admin") {
      return [
        { id: "dashboard", label: "Dashboard" },
        { id: "apply", label: "Apply Leaves" },
        { id: "approve", label: "Approve Leaves" },
        { id: "profile", label: "My Profile" },
        { id: "all-profiles", label: "All Profiles" },
      ];
    } else if (user.role === "manager") {
      return [
        { id: "profile", label: "Dashboard" },
        { id: "apply", label: "Apply Leaves" },
        { id: "approve", label: "Approve Leaves" },
        // { id: 'profile', label: 'My Profile' },
        { id: "all-profiles", label: "Team Profiles" },
      ];
    } else {
      return [
        { id: "profile", label: "Dashboard" },
        { id: "apply", label: "Apply Leaves" },
        // { id: 'profile', label: 'My Profile' }
      ];
    }
  };
  const switchToApplyTab = (leaveId) => {
    setEditingLeaveId(leaveId); // store ID for Apply tab
    setActiveTab("apply"); // switch tab
  };
  return (
    <div className="dashboard-container" data-testid="dashboard">
      <div className="dashboard-header">
        <h1
          data-testid="dashboard-title"
          className="flex items-center gap-3 text-[#E0D4EB] font-semibold text-2xl font-poppins"
        >
          <img
            src="/logo.png"
            alt="Culinary Compass Logo"
            className="w-10 h-10 rounded-full object-contain"
          />
          <span className="leading-tight">
            Culinary <br /> Compass
          </span>
        </h1>

        <div className="user-info">
          <div>
            <div style={{ fontSize: "0.9rem", color: "#718096" }}>
              {user.name}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#a0aec0" }}>
              {user.email}
            </div>
          </div>
          <span className="user-badge" data-testid="user-role-badge">
            {user.role}
          </span>
          <button
            className="logout-btn"
            onClick={onLogout}
            data-testid="logout-btn"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="tabs-container">
          <div className="tabs-list">
            {getTabs().map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === "dashboard" && (
              <DashboardTab
                user={user}
                users={users}
                leaves={leaves}
                onAddUser={() => setShowAddUser(true)}
              />
            )}

            {activeTab === "apply" && (
              <ApplyLeaveTab
                onLeaveApplied={fetchData}
                editingLeaveId={editingLeaveId} // <--- send the leave ID
              />
            )}

            {activeTab === "approve" && (
              <ApproveLeaveTab
                leaves={leaves.filter((l) => l.status === "pending")}
                users={users}
                onApprove={handleApproveLeave}
              />
            )}

            {activeTab === "profile" && (
              <ProfileTab
                user={user}
                currentUser={user}
                from="employee"
                onEditLeave={switchToApplyTab} // <--- send callback
              />
            )}

            {activeTab === "all-profiles" && (
              <AllProfilesTab currentUser={user} />
            )}
          </div>
        </div>
      </div>

      {showAddUser && (
        <AddUserModal
          userRole={user.role}
          userId={user.id}
          onClose={() => setShowAddUser(false)}
          onUserAdded={fetchData}
        />
      )}
    </div>
  );
}

export default Dashboard;
